import {endOfDay, parseISO, set, startOfDay} from 'date-fns';
import {fromZonedTime, toZonedTime} from 'date-fns-tz';

type ISO_STRING = string;

export const JST_TIMEZONE = 'Asia/Tokyo';

export const getCurrentYear = () => new Date().getFullYear();

/**
 * Takes anything as an input and returns true if it is a valid ISO string.
 * example usage:
 * - isISOString('2024-11-19T15:43:58.751719+09:00') // true
 * - isISOString('invalid-string') // false
 */
export const isISOString = (str: unknown): str is ISO_STRING => {
    try {
        const result = parseISO(str as string);
        if (result.toString() === 'Invalid Date') throw new Error();
        return true;
    } catch {
        // eslint-disable-next-line no-console
        console.error('UI : Invalid ISO string', str);
        return false;
    }
};

export const jstDateString = (date: Date | string) => {
    if (typeof date === 'string') if (!isISOString(date)) return '__-__-__';

    return toZonedTime(date, JST_TIMEZONE)
        .toLocaleString('ja-JP', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        })
        .replaceAll('/', '-');
};

export const jstTimeString = (date: Date | string) => {
    if (typeof date === 'string') if (!isISOString(date)) return '__:__';

    return toZonedTime(date, JST_TIMEZONE)
        .toLocaleString('ja-JP', {
            hour: '2-digit',
            minute: '2-digit',
        })
        .replaceAll('/', '-');
};

export const jstDateTimeString = (date: Date | string) => {
    if (typeof date === 'string')
        if (!isISOString(date)) return '__-__-__ __:__';

    const jstDate = jstDateString(date);
    const jstTime = jstTimeString(date);

    return `${jstDate} ${jstTime}`;
};

export const setToStartOfDay = (date: Date | string): Date => {
    const inputZoned = toZonedTime(date, JST_TIMEZONE);
    const outputZoned = startOfDay(inputZoned);

    return fromZonedTime(outputZoned, JST_TIMEZONE);
};

export const setToEndOfDay = (date: Date | string): Date => {
    const inputZoned = toZonedTime(date, JST_TIMEZONE);
    const outputZoned = set(endOfDay(inputZoned), {milliseconds: 0});

    return fromZonedTime(outputZoned, JST_TIMEZONE);
};

export const toUnixTimestamp = (date: Date | null): string =>
    date ? String(Math.floor(date.getTime()) / 1000) : '';

export const fromUnixTimestamp = (unixTimestamp: null | string): Date | null =>
    unixTimestamp ? new Date(Number.parseInt(unixTimestamp, 10) * 1000) : null;
